import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'

import {
  hasAccess,
  hasPendingSubscription,
  isSubscriber,
  type SubscriptionFields,
  trialDaysLeft,
} from '@langmail/auth-config/access'
import { useAuth } from '@langmail/shared-ui'

import { Logo } from '@/components/ui/logo'
import { type Cadence, checkoutUrl, foundingOfferActive, PRICES } from '@/features/billing/subscription'
import { authClient } from '@/lib/auth-client'

const SUPPORT_MAILTO = 'mailto:info@langmail.me?subject=What%20stopped%20you'
const DELETE_OR_EXPORT_MAILTO = 'mailto:info@langmail.me?subject=Delete%20or%20export%20my%20account'
// Under Managed Payments the subscription is sold through Link, so it is
// managed in the customer's Link account, not a Stripe customer portal.
const MANAGE_SUBSCRIPTION_URL = 'https://app.link.com/recurring'

const INCLUDED = [
  'Sovereign mailbox, calendar, and contacts',
  'IMAP, SMTP, JMAP, CalDAV, CardDAV',
  'Agent access over MCP',
  'Morning Brief, classification, to-dos, summaries',
  'Connected Gmail accounts',
  '15 GB storage, full export',
]

const CADENCES: readonly Cadence[] = ['yearly', 'monthly']

// The session cookie is cached for five minutes, so right after checkout the
// context user can still read `trialing` while the webhook has already made
// them `active`. Read the session fresh before offering a checkout: a second
// checkout would create a second subscription and orphan the first.
type SessionUser = SubscriptionFields & { subscription_current_period_end?: unknown }

async function readFreshUser(): Promise<SessionUser | null> {
  const result = await authClient.getSession({ query: { disableCookieCache: true } })
  return result.data?.user ?? null
}

function euro(amount: number): string {
  return `€${Number.isInteger(amount) ? amount : amount.toFixed(2)}`
}

function price(cadence: Cadence, now: Date): { amount: string; per: string; founding: string | null } {
  const p = PRICES[cadence]
  return {
    amount: euro(p.list),
    per: cadence === 'yearly' ? 'year' : 'month',
    founding: foundingOfferActive(now)
      ? `${euro(p.founding)} incl. VAT for your first ${cadence === 'yearly' ? 'year' : 'twelve months'} with the founding offer`
      : null,
  }
}

function renewalDate(value: unknown): string | null {
  if (typeof value === 'string' || value instanceof Date) {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString()
  }
  return null
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh w-dvw items-center justify-center bg-background">
      <div className="w-full max-w-[520px] px-6 py-12 text-center">
        <Logo />
        {children}
      </div>
    </div>
  )
}

// `canceled` is a former subscriber and an expired trial is still `trialing`.
// `none` is neither: a returning subscriber's pending checkout overwrites
// `canceled` with it (subscription-billing.ts), so it must not claim a trial.
function endedHeading(status: unknown): string {
  if (status === 'canceled') {
    return 'Your subscription has ended.'
  }
  if (status === 'trialing') {
    return 'Your trial has ended.'
  }
  return 'Your access has ended.'
}

function introLine({
  ended,
  earlyAccess,
  daysLeft,
}: {
  ended: boolean
  earlyAccess: boolean
  daysLeft: number | null
}): string {
  if (ended) {
    return 'Your mailbox is still here. Subscribe to keep using it.'
  }
  if (earlyAccess) {
    return 'Your early-access account keeps full access. Subscribe whenever you want to support Langmail.'
  }
  if (daysLeft !== null) {
    return `${daysLeft} day${daysLeft === 1 ? '' : 's'} left in your trial. Subscribe whenever you decide to stay.`
  }
  return 'Subscribe whenever you decide to stay.'
}

function PendingExit({ hasAccess: canOpenAccount }: { hasAccess: boolean }) {
  if (canOpenAccount) {
    return (
      <Link className="underline" to="/settings">
        Back to Accounts
      </Link>
    )
  }
  return (
    <a className="underline" href={DELETE_OR_EXPORT_MAILTO}>
      Delete or export my account
    </a>
  )
}

function BackToAccount() {
  return (
    <p className="mt-8 text-sm">
      <Link className="underline" to="/settings">
        Back to Accounts
      </Link>
    </p>
  )
}

export const Subscribe = ({
  now = new Date(),
  refreshUser = readFreshUser,
}: {
  now?: Date
  refreshUser?: () => Promise<SessionUser | null>
}) => {
  const { user: contextUser, signOut } = useAuth()
  const [params] = useSearchParams()
  const [fresh, setFresh] = useState<{ user: SessionUser | null } | null>(null)
  const preselected: Cadence = params.get('plan') === 'monthly' ? 'monthly' : 'yearly'

  useEffect(() => {
    let cancelled = false
    refreshUser()
      .then((freshUser) => {
        if (!cancelled) {
          setFresh({ user: freshUser })
        }
      })
      .catch(() => {
        // Fall back to the context user; the read is a safety net, not a gate.
        if (!cancelled) {
          setFresh({ user: null })
        }
      })
    return () => {
      cancelled = true
    }
  }, [refreshUser])

  if (!contextUser) {
    return null
  }
  if (fresh === null) {
    return (
      <Shell>
        <p className="mt-12 text-muted-foreground text-sm">Checking your plan…</p>
      </Shell>
    )
  }
  const user = { ...contextUser, ...(fresh.user ?? {}) }
  // Early-access accounts (`active`, no stored subscription) keep access and
  // can subscribe like a trialist.
  const subscribed = isSubscriber(user)
  const earlyAccess = user.subscription_status === 'active' && !subscribed
  const ended = !(subscribed || hasAccess(user, now))
  const daysLeft = trialDaysLeft(user, now)

  // A second checkout while the first payment is still clearing would replace
  // the stored id and keep the first subscription billing.
  if (hasPendingSubscription(user)) {
    return (
      <Shell>
        <h1 className="mt-12 mb-3 font-semibold text-2xl text-foreground">Your payment is on its way.</h1>
        <p className="text-muted-foreground text-sm">
          Some payment methods take a few days to clear. Nothing else to do: your plan starts as soon as it does.
        </p>
        {/* Without access (a trial that ended while the payment clears, or a
            returning subscriber) the gate sends every route here, the Accounts tab
            included: offer the same way out as the ended page instead. */}
        <p className="mt-8 text-muted-foreground text-sm">
          <PendingExit hasAccess={hasAccess(user, now)} />
          {' · '}
          <button className="underline" onClick={() => signOut()} type="button">
            Sign out
          </button>
        </p>
      </Shell>
    )
  }

  if (subscribed) {
    // Never show a second checkout: a second subscription would overwrite the
    // stored id and keep the first one billing.
    const renews = renewalDate(user.subscription_current_period_end)
    let detail = 'Thank you for staying.'
    if (user.subscription_status === 'past_due') {
      detail =
        'Your last payment did not go through. Stripe is retrying; update your card under Manage subscription, or from the link in your receipt email.'
    } else if (renews) {
      detail = `Your plan renews on ${renews}.`
    }
    return (
      <Shell>
        <h1 className="mt-12 mb-3 font-semibold text-2xl text-foreground">You're subscribed.</h1>
        <p className="text-muted-foreground text-sm">{detail}</p>
        <a
          className="mt-8 inline-flex w-full items-center justify-center rounded-lg bg-primary px-4 py-2.5 font-medium text-primary-foreground text-sm"
          href={MANAGE_SUBSCRIPTION_URL}
          rel="noopener noreferrer"
          target="_blank"
        >
          Manage subscription
        </a>
        <p className="mt-3 text-muted-foreground text-xs">
          Opens Link, where Stripe keeps your payment details. Sign in with the email you used at checkout.
        </p>
        <BackToAccount />
      </Shell>
    )
  }

  const intro = introLine({ ended, earlyAccess, daysLeft })

  return (
    <Shell>
      <h1 className="mt-12 mb-3 font-semibold text-2xl text-foreground">
        {ended ? endedHeading(user.subscription_status) : 'One plan. Everything included.'}
      </h1>
      <p className="mb-8 text-muted-foreground text-sm">{intro}</p>

      <div className="grid gap-4 sm:grid-cols-2">
        {CADENCES.map((cadence) => {
          const p = price(cadence, now)
          const highlighted = cadence === preselected
          return (
            <div
              className={`rounded-xl border p-5 text-left ${highlighted ? 'border-primary' : 'border-border'}`}
              key={cadence}
            >
              <p className="font-mono text-[11px] text-muted-foreground uppercase tracking-[1.5px]">{cadence}</p>
              <p className="mt-2 font-semibold text-2xl text-foreground">
                {p.amount} <span className="font-normal text-muted-foreground text-sm">/ {p.per} incl. VAT</span>
              </p>
              {p.founding ? <p className="mt-1 text-warning-foreground text-xs">{p.founding}</p> : null}
              <a
                className="mt-4 inline-flex w-full items-center justify-center rounded-lg bg-primary px-4 py-2.5 font-medium text-primary-foreground text-sm"
                href={checkoutUrl({ cadence, userId: user.id, email: user.email, now })}
              >
                Subscribe {cadence}
              </a>
            </div>
          )
        })}
      </div>

      <ul className="mt-8 space-y-1 text-left text-muted-foreground text-sm">
        {INCLUDED.map((item) => (
          <li key={item}>
            <span aria-hidden className="text-success">
              ✓
            </span>{' '}
            {item}
          </li>
        ))}
      </ul>

      {ended ? (
        <p className="mt-8 text-muted-foreground text-sm">
          Not staying?{' '}
          <a className="underline" href={SUPPORT_MAILTO}>
            Tell us what stopped you
          </a>
          .
        </p>
      ) : null}

      {/* The gate sends every other route here, so this page must also be the
          way out: sign out (shared machines, another account), and a way to ask
          for deletion or export without a subscription. */}
      <p className="mt-6 text-muted-foreground text-sm">
        {ended ? (
          <>
            <a className="underline" href={DELETE_OR_EXPORT_MAILTO}>
              Delete or export my account
            </a>
            {' · '}
          </>
        ) : null}
        <button className="underline" onClick={() => signOut()} type="button">
          Sign out
        </button>
      </p>
    </Shell>
  )
}
